import { useEffect, useState } from "react";
import { Bell, CheckCheck, ChevronRight } from "lucide-react";
import { useNavigate } from "../../app/router";
import { readToken, tenantTokenKey } from "../../app/session";
import { useTranslation } from "../../i18n/i18n";
import { ApiError, apiRequest } from "../../lib/api";
import { useTenantApp } from "../app/TenantAppContext";

interface Notification {
  id: string;
  title: string;
  body: string;
  deepLink: string | null;
  readAt: string | null;
  createdAt: string;
  delivery: {
    push: "QUEUED" | "DELIVERED" | "SKIPPED" | "FAILED" | null;
    email: "QUEUED" | "DELIVERED" | "SKIPPED" | "FAILED" | null;
  };
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { refreshUnread } = useTenantApp();
  const { locale, t } = useTranslation();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    try {
      const response = await apiRequest<{ items: Notification[] }>(
        "/api/v1/notifications",
        { accessToken: readToken(tenantTokenKey) },
      );
      setItems(response.items);
      await refreshUnread();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Obavijesti nisu dostupne.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function open(item: Notification) {
    if (!item.readAt) {
      await apiRequest(`/api/v1/notifications/${item.id}/read`, {
        method: "POST",
        accessToken: readToken(tenantTokenKey),
      });
    }
    if (item.deepLink) navigate(item.deepLink);
    else await load();
  }

  async function readAll() {
    await apiRequest("/api/v1/notifications/read-all", {
      method: "POST",
      accessToken: readToken(tenantTokenKey),
    });
    await load();
  }

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));

  return (
    <div className="module-page">
      <header className="module-page__hero">
        <div>
          <span className="eyebrow">IN-APP · PUSH · E-MAIL</span>
          <h1>{t("notifications.title")}</h1>
          <p>{t("notifications.subtitle")}</p>
        </div>
        <button className="button button--ghost" onClick={() => void readAll()}>
          <CheckCheck /> {t("notifications.readAll")}
        </button>
      </header>
      {error && <div className="form-error">{error}</div>}
      <section className="data-card notification-center">
        {items.map((item) => (
          <button
            key={item.id}
            className={item.readAt ? "" : "is-unread"}
            onClick={() => void open(item)}
          >
            <span className="notification-icon">
              <Bell />
            </span>
            <span>
              <strong>{item.title}</strong>
              <p>{item.body}</p>
              <small>{formatDate(item.createdAt)}</small>
              <span className="notification-delivery">
                {item.delivery.push && (
                  <em className={`is-${item.delivery.push.toLowerCase()}`}>
                    PWA:{" "}
                    {item.delivery.push === "DELIVERED"
                      ? "isporučena"
                      : item.delivery.push === "QUEUED"
                        ? "u redu"
                        : item.delivery.push === "FAILED"
                          ? "ponovni pokušaj"
                          : "nije uključena"}
                  </em>
                )}
                {item.delivery.email && (
                  <em className={`is-${item.delivery.email.toLowerCase()}`}>
                    E-mail:{" "}
                    {item.delivery.email === "DELIVERED"
                      ? "poslan"
                      : item.delivery.email === "QUEUED"
                        ? "u redu"
                        : item.delivery.email === "FAILED"
                          ? "ponovni pokušaj"
                          : "nije uključen"}
                  </em>
                )}
              </span>
            </span>
            <ChevronRight />
          </button>
        ))}
        {!loading && !items.length && (
          <div className="table-empty">{t("notifications.empty")}</div>
        )}
      </section>
    </div>
  );
}
